import { cn } from "@/common/helpers/utils";
import { useCSSVariable } from "@/common/hooks/use-css-variable";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import i18next from "i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { AlertCircle, CheckCircle2, MapPin, RefreshCw, Target, X, Zap } from "lucide-react";
import { startTransition, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Circle, Tooltip as LeafletTooltip, MapContainer, Marker, TileLayer, useMapEvents } from "react-leaflet";

interface LocationData {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp?: number;
  isManuallyAdjusted?: boolean;
}

interface LocationMapProps {
  latitude?: number;
  longitude?: number;
  radiusKm?: number;
  accuracy?: number;
  className?: string;
  locations?: Array<{
    latitude: number;
    longitude: number;
    name: string;
    distance?: number;
  }>;
  onLocationClick?: (latitude: number, longitude: number) => void;
  allowLocationAdjustment?: boolean;
  enableLocationControls?: boolean;
  enableRadiusControls?: boolean;
  enableStatistics?: boolean;
  enableAutoLocation?: boolean;
  onLocationChange?: (location: LocationData | null) => void;
  onRadiusChange?: (radiusKm: number) => void;
  onLocationError?: (error: string) => void;
  onCitiesCount?: (count: number) => void;
  showControls?: boolean;
  controlsPosition?: "top" | "bottom" | "left" | "right";
  minRadius?: number;
  maxRadius?: number;
  quickRadiusOptions?: number[];
  title?: string;
  description?: string;
}

const getAccuracyLevel = (accuracy?: number) => {
  if (!accuracy)
    return {
      level: i18next.t("common:Unknown"),
      color: "muted",
      icon: Target,
      description: i18next.t("fit:AccuracyUnknown"),
    };
  if (accuracy <= 5)
    return {
      level: i18next.t("common:VeryHigh"),
      color: ButtonColorVariant.Primary,
      icon: Zap,
      description: i18next.t("masterdata:GPSAccurate5m"),
    };
  if (accuracy <= 15)
    return {
      level: i18next.t("common:High"),
      color: ButtonColorVariant.Primary,
      icon: CheckCircle2,
      description: i18next.t("common:VeryPrecise15m"),
    };
  if (accuracy <= 50)
    return {
      level: i18next.t("common:Medium"),
      color: ButtonColorVariant.Secondary,
      icon: Target,
      description: i18next.t("common:GoodAccuracy50m"),
    };
  if (accuracy <= 100)
    return {
      level: i18next.t("common:Low"),
      color: ButtonColorVariant.Secondary,
      icon: AlertCircle,
      description: i18next.t("common:ModerateAccuracy100m"),
    };
  return {
    level: i18next.t("common:VeryLow"),
    color: ButtonColorVariant.Error,
    icon: X,
    description: i18next.t("common:Inaccurate100m"),
  };
};

const formatTimestamp = (timestamp?: number) => {
  if (!timestamp) return "";
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes === 0) return i18next.t("common:JustNow");
  if (minutes === 1) return i18next.t("common:OneMinuteAgo");
  return i18next.t("common:MinutesAgo", { minutes });
};

function MapController({
  latitude,
  longitude,
  radiusKm,
  onLocationClick,
  allowLocationAdjustment,
}: {
  latitude: number;
  longitude: number;
  radiusKm?: number;
  onLocationClick?: (latitude: number, longitude: number) => void;
  allowLocationAdjustment?: boolean;
}) {
  const map = useMapEvents({
    click: (e) => {
      if (allowLocationAdjustment && onLocationClick) {
        onLocationClick(e.latlng.lat, e.latlng.lng);
      }
    },
  });

  useEffect(() => {
    if (map?.getContainer) {
      const container = map.getContainer();
      if (allowLocationAdjustment) {
        container.style.cursor = "crosshair";
      } else {
        container.style.cursor = "";
      }
    }
  }, [map, allowLocationAdjustment]);

  useEffect(() => {
    if (!map || !latitude || !longitude) return;

    const frameId = requestAnimationFrame(() => {
      try {
        if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
          console.warn("Invalid coordinates provided to map");
          return;
        }

        if (!radiusKm || Number.isNaN(radiusKm)) {
          if (map?.setView && !Number.isNaN(latitude) && !Number.isNaN(longitude)) {
            map.setView([latitude, longitude], 13);
          }
          return;
        }

        const latOffset = radiusKm / 111;
        const lngOffset = radiusKm / (111 * Math.cos((latitude * Math.PI) / 180));

        const bounds = L.latLngBounds([
          [latitude - latOffset, longitude - lngOffset],
          [latitude + latOffset, longitude + lngOffset],
        ]);

        const paddedBounds = bounds.pad(0.2);

        if (map?.getContainer?.() && map.getSize()?.x > 0) {
          map.fitBounds(paddedBounds, { animate: false });
        }
      } catch (error) {
        console.warn("Error fitting map bounds:", error);
        try {
          if (map?.setView && !Number.isNaN(latitude) && !Number.isNaN(longitude)) {
            const zoom = Math.max(10, Math.min(15, 16 - Math.log2(radiusKm || 50)));
            map.setView([latitude, longitude], zoom);
          }
        } catch (fallbackError) {
          console.warn("Error setting map view:", fallbackError);
        }
      }
    });

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [map, latitude, longitude, radiusKm]);

  return null;
}

export function LocationMap({
  latitude: initialLatitude,
  longitude: initialLongitude,
  radiusKm: initialRadiusKm,
  accuracy,
  className,
  locations = [],
  onLocationClick,
  allowLocationAdjustment = false,
  enableLocationControls = false,
  enableRadiusControls = false,
  enableStatistics = false,
  enableAutoLocation = false,
  onLocationChange,
  onRadiusChange,
  onLocationError,
  onCitiesCount,
  controlsPosition = "left",
  minRadius = 5,
  maxRadius = 200,
  quickRadiusOptions = [10, 25, 50, 100],
  title,
  description,
}: LocationMapProps) {
  const mapRef = useRef<L.Map>(null);

  const [userLocation, setUserLocation] = useState<LocationData | null>(() => {
    if (initialLatitude && initialLongitude) {
      return {
        latitude: initialLatitude,
        longitude: initialLongitude,
        accuracy,
      };
    }
    return null;
  });

  const [radiusKm, setRadiusKm] = useState<number>(initialRadiusKm);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [showLocationAdjustment, setShowLocationAdjustment] = useState(false);
  const radiusSliderId = useId();
  const primaryColor = useCSSVariable("--primary");
  const secondaryColor = useCSSVariable("--secondary");
  const errorColor = useCSSVariable("--error");
  const mutedForegroundColor = useCSSVariable("--muted-foreground");

  const currentLocation = useMemo(
    () =>
      userLocation ||
      (initialLatitude && initialLongitude
        ? {
            latitude: initialLatitude,
            longitude: initialLongitude,
            accuracy,
          }
        : null),
    [userLocation, initialLatitude, initialLongitude, accuracy],
  );

  const currentRadius = initialRadiusKm || radiusKm;

  const validLatitude =
    currentLocation?.latitude && !Number.isNaN(currentLocation.latitude) ? currentLocation.latitude : 0;
  const validLongitude =
    currentLocation?.longitude && !Number.isNaN(currentLocation.longitude) ? currentLocation.longitude : 0;
  const validRadiusKm =
    typeof currentRadius === "number" && !Number.isNaN(currentRadius) && currentRadius > 0 ? currentRadius : 0;
  const safeLocations = Array.isArray(locations) ? locations : [];

  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      const error = i18next.t("common:GeolocationNotSupported");
      setLocationError(error);
      onLocationError?.(error);
      return;
    }

    setIsGettingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation: LocationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        };
        setUserLocation(newLocation);
        setIsGettingLocation(false);
        onLocationChange?.(newLocation);
      },
      () => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const newLocation: LocationData = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp,
            };
            setUserLocation(newLocation);
            setIsGettingLocation(false);
            onLocationChange?.(newLocation);
          },
          (fallbackError) => {
            let errorMessage = i18next.t("common:ErrorGettingPosition");
            switch (fallbackError.code) {
              case fallbackError.PERMISSION_DENIED:
                errorMessage = i18next.t("common:GeolocationAccessHasBeenDenied");
                break;
              case fallbackError.POSITION_UNAVAILABLE:
                errorMessage = i18next.t("masterdata:LocationUnavailable");
                break;
              case fallbackError.TIMEOUT:
                errorMessage = i18next.t("masterdata:LocationTimeout");
                break;
            }
            setLocationError(errorMessage);
            setIsGettingLocation(false);
            onLocationError?.(errorMessage);
          },
          {
            enableHighAccuracy: false,
            timeout: 15000,
            maximumAge: 600000,
          },
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 60000,
      },
    );
  }, [onLocationChange, onLocationError]);

  const clearLocation = useCallback(() => {
    setUserLocation(null);
    setLocationError(null);
    setShowLocationAdjustment(false);
    onLocationChange?.(null);
  }, [onLocationChange]);

  const handleLocationAdjustment = useCallback(
    (lat: number, lng: number) => {
      if (currentLocation) {
        const adjustedLocation: LocationData = {
          ...currentLocation,
          latitude: lat,
          longitude: lng,
          isManuallyAdjusted: true,
          timestamp: Date.now(),
        };
        setUserLocation(adjustedLocation);
        setShowLocationAdjustment(false);
        onLocationChange?.(adjustedLocation);
      }
      onLocationClick?.(lat, lng);
    },
    [currentLocation, onLocationChange, onLocationClick],
  );

  const handleRadiusChange = useCallback(
    (newRadius: number) => {
      setRadiusKm(newRadius);
      onRadiusChange?.(newRadius);
    },
    [onRadiusChange],
  );

  useEffect(() => {
    if (enableAutoLocation && !currentLocation) {
      startTransition(getUserLocation);
    }
  }, [enableAutoLocation, currentLocation, getUserLocation]);

  useEffect(() => {
    onCitiesCount?.(safeLocations.length);
  }, [safeLocations.length, onCitiesCount]);

  if (validLatitude === 0 && validLongitude === 0) {
    if (enableLocationControls) {
      return (
        <div className={`relative ${className} space-y-4`}>
          <div className="bg-muted/20 rounded-lg p-6 text-center">
            <div className="text-muted-foreground mb-4 text-sm">
              {title && <div className="text-foreground mb-1 font-medium">{title}</div>}
              {description && <div className="mb-4">{description}</div>}
              {i18next.t("common:NoValidCoordinates")}
            </div>

            {locationError && (
              <Alert variant={ButtonColorVariant.Error} className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">{locationError}</AlertDescription>
              </Alert>
            )}

            <Button
              onClick={getUserLocation}
              disabled={isGettingLocation}
              size={ButtonSize.Default}
              className="w-full max-w-sm"
            >
              {isGettingLocation ? (
                <>
                  <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor className="mr-2" />
                  {i18next.t("masterdata:GettingGPSPosition")}
                </>
              ) : (
                <>
                  <MapPin className="mr-2 h-4 w-4" />
                  {i18next.t("common:DetermineLocation")}
                </>
              )}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className={`relative ${className} bg-muted/20 flex items-center justify-center rounded-lg`}>
        <div className="text-muted-foreground text-center">
          <div className="text-sm">{i18next.t("common:NoValidCoordinates")}</div>
        </div>
      </div>
    );
  }

  const createCityIcon = (distance?: number) => {
    let color = errorColor;
    let size = 8;

    if (typeof distance === "number" && !Number.isNaN(distance) && validRadiusKm) {
      if (distance <= validRadiusKm * 0.3) {
        color = primaryColor;
        size = 16;
      } else if (distance <= validRadiusKm * 0.6) {
        color = secondaryColor;
        size = 14;
      } else {
        color = errorColor;
        size = 12;
      }
    }

    return L.divIcon({
      className: "city-marker",
      html: `<div style="
        width: ${size}px;
        height: ${size}px;
        background-color: ${color};
        border: 3px solid white;
        border-radius: 50%;
      "></div>`,
      iconSize: [size + 6, size + 6],
      iconAnchor: [(size + 6) / 2, (size + 6) / 2],
    });
  };

  const renderLocationControls = () => {
    if (!enableLocationControls) return null;

    return (
      <div className="bg-background/95 space-y-4 rounded-lg border p-4 shadow-lg backdrop-blur-sm dark:shadow-none">
        <div className="space-y-1">
          <h3 className="text-sm font-medium">{title}</h3>
          {description && <p className="text-muted-foreground text-xs">{description}</p>}
        </div>

        {currentLocation ? (
          <div className="space-y-3">
            <div className="text-muted-foreground bg-muted/50 flex items-center gap-2 rounded-md px-3 py-2 text-sm">
              <CheckCircle2 className="text-primary h-4 w-4" />
              <span>
                {currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}
              </span>
              {currentLocation.accuracy && (
                <div className="ml-auto flex items-center gap-1">
                  {(() => {
                    const { icon: Icon, description: desc, color } = getAccuracyLevel(currentLocation.accuracy);
                    return (
                      <>
                        <Icon
                          className={`h-3 w-3 ${
                            color === ButtonColorVariant.Primary
                              ? "text-primary"
                              : color === ButtonColorVariant.Secondary
                                ? "text-secondary"
                                : color === ButtonColorVariant.Error
                                  ? "text-error"
                                  : "text-muted-foreground"
                          }`}
                        />
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-xs">±{Math.round(currentLocation.accuracy)}m</span>
                            </TooltipTrigger>
                            <TooltipContent>{desc}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={getUserLocation}
                disabled={isGettingLocation}
                size={ButtonSize.Sm}
                variant={ButtonVariant.Filled}
                colorVariant={ButtonColorVariant.Secondary}
                className="flex-1"
              >
                {isGettingLocation ? (
                  <>
                    <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor className="mr-2" />
                    {i18next.t("masterdata:Updating")}
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {i18next.t("common:Update")}
                  </>
                )}
              </Button>
              <Button
                variant={ButtonVariant.Outlined}
                colorVariant={ButtonColorVariant.Secondary}
                size={ButtonSize.Sm}
                onClick={clearLocation}
              >
                <X className="mr-2 h-4 w-4" />
                {i18next.t("ram:Delete")}
              </Button>
            </div>

            {currentLocation.accuracy && currentLocation.accuracy > 50 && (
              <Alert className="py-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  <div className="flex items-center justify-between">
                    <span>
                      <strong>
                        {i18next.t("masterdata:LowAccuracy", { accuracy: Math.round(currentLocation.accuracy) })}
                      </strong>{" "}
                      {i18next.t("common:ForBetterGPSAccuracy")}
                    </span>
                    <Button
                      size={ButtonSize.Sm}
                      variant={ButtonVariant.Outlined}
                      colorVariant={ButtonColorVariant.Secondary}
                      onClick={() => setShowLocationAdjustment(true)}
                      className="ml-2 h-6 px-2 text-xs"
                    >
                      {i18next.t("common:Adjust")}
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {locationError && (
              <Alert variant={ButtonColorVariant.Error} className="py-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">{locationError}</AlertDescription>
              </Alert>
            )}

            <Button onClick={getUserLocation} disabled={isGettingLocation} size={ButtonSize.Default} className="w-full">
              {isGettingLocation ? (
                <>
                  <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor className="mr-2" />
                  {i18next.t("masterdata:GettingGPSPosition")}
                </>
              ) : (
                <>
                  <MapPin className="mr-2 h-4 w-4" />
                  {i18next.t("common:DetermineLocation")}
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderRadiusControls = () => {
    if (!enableRadiusControls) return null;

    return (
      <div className="bg-background/95 space-y-3 rounded-lg border p-4 shadow-lg backdrop-blur-sm dark:shadow-none">
        <div className="flex items-center justify-between">
          <Label htmlFor={radiusSliderId} className="text-sm font-medium">
            {i18next.t("common:SearchRadius")}
          </Label>
          <Badge variant={BadgeVariant.Filled} colorVariant={BadgeColorVariant.Secondary} className="text-xs">
            {validRadiusKm ? `${validRadiusKm} km` : i18next.t("common:NotSet")}
          </Badge>
        </div>

        <Slider
          id={radiusSliderId}
          min={minRadius}
          max={maxRadius}
          step={5}
          value={[validRadiusKm]}
          onValueChange={(value) => handleRadiusChange(value[0])}
          className="w-full"
        />

        <div className="grid grid-cols-2 gap-2">
          {quickRadiusOptions.map((radius) => (
            <Button
              key={radius}
              variant={validRadiusKm === radius ? ButtonVariant.Filled : ButtonVariant.Outlined}
              colorVariant={validRadiusKm === radius ? ButtonColorVariant.Primary : ButtonColorVariant.Secondary}
              size={ButtonSize.Sm}
              onClick={() => handleRadiusChange(radius)}
              className="text-xs"
            >
              {radius} km
            </Button>
          ))}
        </div>
      </div>
    );
  };

  const renderStatistics = () => {
    if (!enableStatistics) return null;

    return (
      <div className="bg-background/95 space-y-2 rounded-lg border p-4 shadow-lg backdrop-blur-sm dark:shadow-none">
        <div className="text-muted-foreground text-xs font-medium">{i18next.t("common:Statistics").toUpperCase()}</div>
        <div className="flex items-center justify-between text-sm">
          <span>{i18next.t("common:FoundObjects")}:</span>
          <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Secondary} className="text-xs">
            {safeLocations.length}
          </Badge>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span>{i18next.t("common:SearchRadius")}:</span>
          <span className="text-muted-foreground text-xs">
            {validRadiusKm ? `${validRadiusKm} km` : i18next.t("common:NotSet")}
          </span>
        </div>
        {currentLocation?.accuracy && (
          <div className="flex items-center justify-between text-sm">
            <span>{i18next.t("common:GPSAccuracy")}:</span>
            <div className="flex items-center gap-1">
              {(() => {
                const { icon: Icon, level, color } = getAccuracyLevel(currentLocation.accuracy);
                return (
                  <>
                    <Icon
                      className={`h-3 w-3 ${
                        color === ButtonColorVariant.Primary
                          ? "text-primary"
                          : color === ButtonColorVariant.Secondary
                            ? "text-secondary"
                            : color === ButtonColorVariant.Error
                              ? "text-error"
                              : "text-muted-foreground"
                      }`}
                    />
                    <span className="text-muted-foreground text-xs">{level}</span>
                  </>
                );
              })()}
            </div>
          </div>
        )}
        {currentLocation?.timestamp && (
          <div className="flex items-center justify-between text-sm">
            <span>{i18next.t("common:LastUpdate")}:</span>
            <span className="text-muted-foreground text-xs">{formatTimestamp(currentLocation.timestamp)}</span>
          </div>
        )}
        {currentLocation?.isManuallyAdjusted && (
          <div className="flex items-center justify-between text-sm">
            <span>{i18next.t("common:Status")}:</span>
            <span className="text-primary text-xs">{i18next.t("common:ManuallyAdjusted")}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`relative ${className}`}>
      {/* Controls */}
      {(enableLocationControls || enableRadiusControls || enableStatistics) && (
        <div
          className={`absolute z-50 space-y-2 ${
            controlsPosition === "top"
              ? "top-2 right-2 left-2"
              : controlsPosition === "bottom"
                ? "right-2 bottom-2 left-2"
                : controlsPosition === "right"
                  ? "top-2 right-2 w-80"
                  : "top-2 left-2 w-80"
          }`}
        >
          {enableLocationControls && renderLocationControls()}
          {enableRadiusControls && renderRadiusControls()}
          {enableStatistics && renderStatistics()}
        </div>
      )}

      {/* Map Container */}
      <div className={`relative z-0 h-full w-full`}>
        <MapContainer
          ref={mapRef}
          center={[validLatitude, validLongitude]}
          zoom={13}
          style={{
            height: "100%",
            width: "100%",
            borderRadius: "0.5rem",
            cursor: allowLocationAdjustment || showLocationAdjustment ? "crosshair" : "grab",
          }}
          zoomControl={true}
          scrollWheelZoom={false}
        >
          <TileLayer
            className="z-0"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          <Marker position={[validLatitude, validLongitude]} />

          {validRadiusKm && (
            <Circle
              center={[validLatitude, validLongitude]}
              radius={validRadiusKm * 1000}
              pathOptions={{
                color: primaryColor,
                fillColor: primaryColor,
                fillOpacity: 0.1,
                weight: 2,
              }}
            />
          )}

          {currentLocation?.accuracy &&
            typeof currentLocation.accuracy === "number" &&
            !Number.isNaN(currentLocation.accuracy) && (
              <Circle
                center={[validLatitude, validLongitude]}
                radius={currentLocation.accuracy}
                pathOptions={{
                  color: `hsl(${mutedForegroundColor})`,
                  fillColor: `hsl(${mutedForegroundColor})`,
                  fillOpacity: 0.2,
                  weight: 1,
                  dashArray: "5, 5",
                }}
              />
            )}

          {safeLocations.map((city) => {
            const cityLat = typeof city?.latitude === "number" && !Number.isNaN(city.latitude) ? city.latitude : null;
            const cityLng =
              typeof city?.longitude === "number" && !Number.isNaN(city.longitude) ? city.longitude : null;
            const cityName = typeof city?.name === "string" ? city.name : i18next.t("common:UnknownCity");

            if (cityLat === null || cityLng === null) return null;

            return (
              <Marker
                key={`city-${cityLat.toFixed(6)}-${cityLng.toFixed(6)}-${cityName.replace(/\s+/g, "-")}`}
                position={[cityLat, cityLng]}
                icon={createCityIcon(city.distance)}
              >
                <LeafletTooltip direction="top" offset={[0, -10]} opacity={0.9}>
                  <div className="text-xs">
                    <div className="font-medium">{cityName}</div>
                    {typeof city.distance === "number" && !Number.isNaN(city.distance) && (
                      <div className="text-muted-foreground">
                        {i18next.t("common:KmAway", { distance: city.distance.toFixed(1) })}
                      </div>
                    )}
                  </div>
                </LeafletTooltip>
              </Marker>
            );
          })}

          <MapController
            latitude={validLatitude}
            longitude={validLongitude}
            radiusKm={validRadiusKm}
            onLocationClick={handleLocationAdjustment}
            allowLocationAdjustment={allowLocationAdjustment || showLocationAdjustment}
          />
        </MapContainer>
      </div>

      {/* Bottom overlay info */}
      <div className="absolute bottom-2 left-2 z-40 space-y-1">
        {(allowLocationAdjustment || showLocationAdjustment) && (
          <div
            className={cn(
              "bg-primary text-primary-foreground rounded-md px-3 py-2 text-xs font-medium shadow-lg",
              "border-primary-foreground/20 animate-pulse border-2 dark:shadow-none",
            )}
          >
            🎯 {i18next.t("common:ClickMapToAdjustLocation")}
          </div>
        )}
        {!enableStatistics &&
          currentLocation?.accuracy &&
          typeof currentLocation.accuracy === "number" &&
          !Number.isNaN(currentLocation.accuracy) && (
            <div className="bg-background border-border text-foreground rounded border-2 px-2 py-1 text-xs shadow-lg dark:shadow-none">
              {i18next.t("common:AccuracyLabel", { accuracy: Math.round(currentLocation.accuracy) })}
            </div>
          )}
        {!enableStatistics && safeLocations.length > 0 && (
          <div className="bg-background border-border text-foreground rounded border-2 px-2 py-1 text-xs shadow-lg dark:shadow-none">
            {i18next.t("common:ObjectsFound", { count: safeLocations.length })}
          </div>
        )}
      </div>

      {/* Legend */}
      {safeLocations.length > 0 && !enableStatistics && validRadiusKm && (
        <div
          className={cn(
            "bg-background border-border absolute top-2 right-2 rounded-lg border-2 px-4 py-3 text-xs",
            "z-40 space-y-2",
          )}
        >
          <div className="text-foreground border-border mb-2 border-b pb-1 text-sm font-bold">
            {i18next.t("common:Distance")}
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-primary border-background h-4 w-4 rounded-full border-2 shadow-md dark:shadow-none"></div>
            <span className="text-foreground font-medium">
              {i18next.t("common:NearDistance", { min: 0, max: Math.round(validRadiusKm * 0.3) })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-secondary border-background h-4 w-4 rounded-full border-2 shadow-md dark:shadow-none"></div>
            <span className="text-foreground font-medium">
              {i18next.t("common:MediumDistance", {
                min: Math.round(validRadiusKm * 0.3),
                max: Math.round(validRadiusKm * 0.6),
              })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-error border-background h-4 w-4 rounded-full border-2 shadow-md dark:shadow-none"></div>
            <span className="text-foreground font-medium">
              {i18next.t("common:FarDistance", { min: Math.round(validRadiusKm * 0.6) })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
